import { useState } from "react";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CollectionTab } from "./CollectionTab";

// Journal tab = AI article drafter + the articles collection manager.
// (The autonomous SEO Agent now has its own subpage at /admin/seo-agent.)
export const JournalTab = () => {
  const [keyword, setKeyword] = useState("");
  const [cluster, setCluster] = useState("");
  const [busy, setBusy] = useState(false);
  const [draftKey, setDraftKey] = useState(0);

  const generate = async () => {
    if (keyword.trim().length < 3) {
      toast.error("Type a target keyword first — e.g. 'pigmentation treatment dubai'");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/articles/ai-draft", { keyword: keyword.trim(), cluster: cluster.trim() }, { timeout: 200000 });
      toast.success(`Draft ready: "${data.title}" — review it below, then Publish`);
      setKeyword("");
      setCluster("");
      setDraftKey((k) => k + 1); // remount the list so the new draft appears
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Drafting failed — please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="glass rounded-2xl p-6" data-testid="ai-drafter">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B38B2D]">
            <Wand2 className="h-5 w-5 text-white" aria-hidden="true" />
          </span>
          <div>
            <h2 className="font-serif text-xl font-semibold text-[#1A1615]">AI Article Drafter</h2>
            <p className="text-xs font-light text-[#91837C]">
              Type a Dubai keyword — get a complete, SEO-structured draft in your brand voice with an AI cover image.
              It saves as a <b>Draft</b> (hidden from the site) until you review and publish it.
            </p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_240px_auto]">
          <input
            className="input-lux text-sm"
            placeholder="Target keyword, e.g. hydrafacial dubai price"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && generate()}
            data-testid="ai-drafter-keyword"
            disabled={busy}
          />
          <input
            className="input-lux text-sm"
            placeholder="Topic cluster (optional)"
            value={cluster}
            onChange={(e) => setCluster(e.target.value)}
            data-testid="ai-drafter-cluster"
            disabled={busy}
          />
          <button onClick={generate} disabled={busy} data-testid="ai-drafter-generate" className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 text-xs disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Drafting — about a minute…" : "Draft Article"}
          </button>
        </div>
        {busy && (
          <p className="mt-3 text-[11px] font-light text-[#91837C]" data-testid="ai-drafter-progress">
            Writing the essay, crafting the SEO fields and painting the cover image — this takes 30–90 seconds.
          </p>
        )}
      </div>

      <CollectionTab key={draftKey} collection="articles" />
    </div>
  );
};
